import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/shared/lib/utils";
import { ensureFontLoaded, resolveFontFamily } from "../../lib/shareFonts";
import { resolveColorValue, resolveTextValue, resolveTitleValue, findImagePreset, } from "../../lib/resolveConfig";
import { PresetImage } from "../PresetImage";
import styles from "./SharePreview.module.scss";
const DEFAULT_BG = "#141018";
const MAX_PHOTOS = 20;
const SLIDE_MS = 5000;
/** Слой тайловых паттернов позади контента. */
const PatternLayer = ({ patterns, presets }) => {
    const layerRef = React.useRef(null);
    const [cells, setCells] = React.useState(0);
    const tile = Math.min(120, Math.max(24, Math.round(patterns?.pattern_size || 48)));
    const variety = Math.min(patterns?.preset_ids?.length ?? 0, Math.max(1, patterns?.pattern_variety || 1));
    const chosen = React.useMemo(() => {
        if (!patterns?.preset_ids?.length)
            return [];
        return patterns.preset_ids
            .slice(0, variety)
            .map((id) => findImagePreset(id, presets.patterns))
            .filter(Boolean);
    }, [patterns, presets.patterns, variety]);
    React.useEffect(() => {
        if (!chosen.length) {
            setCells(0);
            return;
        }
        const el = layerRef.current;
        if (!el)
            return;
        const update = () => {
            const w = el.clientWidth || window.innerWidth;
            const h = el.clientHeight || window.innerHeight;
            const cols = Math.max(1, Math.ceil(w / tile) + 1);
            const rows = Math.max(1, Math.ceil(h / tile) + 1);
            // Запас на весь экран; верхняя граница — чтобы не раздувать DOM.
            setCells(Math.min(cols * rows, 2500));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [tile, chosen.length]);
    if (!chosen.length) {
        return null;
    }
    return (_jsx("div", { ref: layerRef, className: styles.patterns, style: { ["--tile"]: `${tile}px` }, "aria-hidden": "true", children: Array.from({ length: cells }).map((_, i) => {
            const preset = chosen[i % chosen.length];
            return (_jsx(PresetImage, { preset: preset, className: styles.patternCell, decorative: true }, i));
        }) }));
};
const PhotoGallery = ({ photos }) => {
    const list = React.useMemo(() => (photos ?? []).filter(Boolean).slice(0, MAX_PHOTOS), [photos]);
    const [index, setIndex] = React.useState(0);
    React.useEffect(() => {
        setIndex(0);
    }, [list]);
    React.useEffect(() => {
        if (list.length <= 1)
            return;
        const id = window.setInterval(() => {
            setIndex((i) => (i + 1) % list.length);
        }, SLIDE_MS);
        return () => window.clearInterval(id);
    }, [list]);
    if (!list.length)
        return null;
    const current = list[Math.min(index, list.length - 1)];
    return (_jsx("div", { className: styles.photos, "aria-live": "polite", children: _jsx("div", { className: styles.photoFrame, children: _jsx(AnimatePresence, { initial: false, children: _jsx(motion.div, { className: styles.photo, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.8, ease: "easeInOut" }, children: _jsx("img", { src: current, alt: "", loading: "lazy", draggable: false }) }, current) }) }) }));
};
const GiftContent = ({ config, presets }) => {
    const occasion = resolveTitleValue(config.occasion, presets.occasions);
    const icon = config.icon
        ? findImagePreset(config.icon.preset_id, presets.icons)
        : undefined;
    const greetingText = config.greeting
        ? resolveTextValue(config.greeting.text, presets.greetings)
        : undefined;
    const greetingColor = config.greeting
        ? resolveColorValue(config.greeting.text_color, presets.text_colors)
        : undefined;
    const wish = resolveTextValue(config.wish, presets.wishes);
    React.useEffect(() => {
        ensureFontLoaded(config.greeting?.font_key);
    }, [config.greeting?.font_key]);
    return (_jsxs("div", { className: styles.content, children: [(icon || occasion) && (_jsxs("div", { className: styles.occasion, children: [icon && _jsx("span", { className: styles.icon, children: _jsx(PresetImage, { preset: icon }) }), occasion && _jsx("span", { className: styles.occasionText, children: occasion })] })), greetingText && (_jsx("div", { className: styles.greeting, style: {
                    fontFamily: resolveFontFamily(config.greeting?.font_key),
                    color: greetingColor,
                    fontSize: config.greeting?.text_size
                        ? Math.min(80, Math.max(14, config.greeting.text_size))
                        : undefined,
                }, children: greetingText })), wish && _jsx("div", { className: styles.wish, children: wish }), _jsx(PhotoGallery, { photos: config.photos })] }));
};
const SimpleContent = ({ config, presets }) => {
    const captionColor = config.caption
        ? resolveColorValue(config.caption.text_color, presets.text_colors)
        : undefined;
    React.useEffect(() => {
        ensureFontLoaded(config.caption?.font_key);
    }, [config.caption?.font_key]);
    return (_jsxs("div", { className: styles.content, children: [config.caption?.text && (_jsx("div", { className: styles.greeting, style: {
                    fontFamily: resolveFontFamily(config.caption.font_key),
                    color: captionColor,
                    fontSize: config.caption.text_size
                        ? Math.min(80, Math.max(14, config.caption.text_size))
                        : undefined,
                }, children: config.caption.text })), _jsx(PhotoGallery, { photos: config.photos })] }));
};
/**
 * Рендер оформления шары (без коробки/плеера) — используется и в живом превью
 * редактора, и на публичной странице после «открытия». Фон, паттерны, тексты
 * (шрифт/цвет/размер по config), повод+иконка, фото.
 */
export const SharePreview = ({ type, config, presets, className, fit = false, }) => {
    const bg = resolveColorValue(config.background, presets.backgrounds) ?? DEFAULT_BG;
    return (_jsxs("div", { className: cn(styles.canvas, fit && styles.canvasFit, className), style: { background: bg }, children: [_jsx(PatternLayer, { patterns: config.patterns, presets: presets }), type === "gift_page" ? (_jsx(GiftContent, { config: config, presets: presets })) : (_jsx(SimpleContent, { config: config, presets: presets }))] }));
};
